import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/site/Logo";
import { api, errorMessage } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const STATUSES = ["nouveau", "en cours", "traité", "archivé"];
const FILTERS = [["", "Toutes"], ["diagnostic", "Diagnostics"], ["contact", "Contacts"]];

function LeadDetail({ l }) {
  const rows = [["Entreprise", l.company], ["Secteur", l.sector], ["Ville", l.city], ["Téléphone", l.phone], ["Site", l.website], ["Fiche Google", l.google_profile], ["Objectifs", l.goals?.join(", ")], ["Objet", l.subject]];
  return (
    <div className="grid gap-x-8 gap-y-2 bg-paper p-5 text-[14px] md:grid-cols-2">
      {rows.filter(([, v]) => v).map(([k, v]) => <p key={k}><span className="text-mute">{k} : </span>{v}</p>)}
      {l.message && <p className="whitespace-pre-wrap md:col-span-2"><span className="text-mute">Message : </span>{l.message}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  useSeo({ title: "Demandes reçues", description: "Accès réservé.", noindex: true });
  const nav = useNavigate();
  const [leads, setLeads] = useState(null);
  const [kind, setKind] = useState("");
  const [open, setOpen] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/leads", { params: kind ? { kind } : {} });
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      if ([401, 403].includes(err.response?.status)) nav("/admin/connexion");
      else setError(errorMessage(err));
    }
  }, [kind, nav]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { await api.patch(`/admin/leads/${id}`, { status }); load(); };
  const remove = async (id) => { if (window.confirm("Supprimer définitivement cette demande ?")) { await api.delete(`/admin/leads/${id}`); load(); } };
  const logout = async () => { await api.post("/auth/logout"); nav("/admin/connexion"); };

  return (
    <main className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="border-b border-ink/10"><div className="wrap flex h-20 items-center justify-between"><Logo /><button onClick={logout} className="link-u text-sm font-semibold" data-testid="admin-logout">Se déconnecter</button></div></header>
      <div className="wrap py-12">
        <h1 className="display text-[3rem]">Demandes reçues</h1>
        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map(([v, l]) => (
            <button key={l} onClick={() => setKind(v)} aria-pressed={kind === v} data-testid={`admin-filter-${v || "all"}`} className={`rounded-full border px-4 py-2 text-sm ${kind === v ? "border-ink bg-ink text-ivory" : "border-ink/20"}`}>{l}</button>
          ))}
        </div>
        {error && <p role="alert" className="mt-6 text-red-800">{error}</p>}
        {leads === null ? <p className="mt-10 text-mute">Chargement…</p> : !leads.length ? <p className="mt-10 text-mute" data-testid="admin-empty">Aucune demande pour le moment.</p> : (
          <ul className="mt-10 border-t border-ink/15" data-testid="admin-leads">
            {leads.map((l) => (
              <li key={l.lead_id} className="border-b border-ink/15 py-4" data-testid={`lead-${l.lead_id}`}>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                  <button onClick={() => setOpen(open === l.lead_id ? null : l.lead_id)} className="min-w-[220px] flex-1 text-left" data-testid={`lead-toggle-${l.lead_id}`}>
                    <span className="mr-3 bg-paper px-2.5 py-0.5 font-mono text-[12px] uppercase tracking-wider text-umber">{l.kind}</span>
                    <span className="font-semibold">{l.name}</span> <span className="text-mute">— {l.company || l.email}</span>
                    <span className="block text-[13px] text-mute">{new Date(l.created_at).toLocaleString("fr-FR")} · <a href={`mailto:${l.email}`} className="underline">{l.email}</a> · e-mail {l.email_status === "sent" ? "envoyé" : l.email_status === "failed" ? "non envoyé" : "en attente"}</span>
                  </button>
                  <select aria-label="Statut" value={l.status} onChange={(e) => setStatus(l.lead_id, e.target.value)} className="border border-ink/20 bg-transparent px-3 py-2 text-sm" data-testid={`lead-status-${l.lead_id}`}>
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(l.lead_id)} className="text-sm text-red-800 underline" data-testid={`lead-delete-${l.lead_id}`}>Supprimer</button>
                </div>
                {open === l.lead_id && <div className="mt-4"><LeadDetail l={l} /></div>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
